import type { IParseCsvProps, IParsedCsv } from "./parse-csv.types";

const DOUBLE_QUOTE = '"';
const CARRIAGE_RETURN = "\r";
const LINE_FEED = "\n";

// NOTE: The delimiters the app's own CSV generator and common exports use. The
// first record decides: whichever of these appears most often outside quotes
// wins, with the comma as the tie-break.
const CANDIDATE_DELIMITERS = [",", ";", "\t"] as const;

const ALPHABET_SIZE = 26;
const LETTER_A_CODE_POINT = 65;

interface ISniffDelimiterProps {
  text: string;
  limit: number;
}

const sniffDelimiter = ({ text, limit }: ISniffDelimiterProps): string => {
  const counts = new Map<string, number>(
    CANDIDATE_DELIMITERS.map((delimiter) => [delimiter, 0]),
  );
  let isQuoted = false;

  for (let index = 0; index < limit; index += 1) {
    const character = text[index] ?? "";
    if (character === DOUBLE_QUOTE) {
      isQuoted = !isQuoted;
      continue;
    }
    if (
      !isQuoted &&
      (character === LINE_FEED || character === CARRIAGE_RETURN)
    ) {
      break;
    }
    if (!isQuoted && counts.has(character)) {
      counts.set(character, (counts.get(character) ?? 0) + 1);
    }
  }

  let best: string = CANDIDATE_DELIMITERS[0];
  let bestCount = counts.get(best) ?? 0;
  for (const delimiter of CANDIDATE_DELIMITERS) {
    const count = counts.get(delimiter) ?? 0;
    if (count > bestCount) {
      best = delimiter;
      bestCount = count;
    }
  }
  return best;
};

// NOTE: NSAI-4326 — a small RFC 4180 reader for previewing a generated .csv on
// a client that has no SheetJS (mobile). Quoted fields may hold the delimiter,
// line breaks and doubled quotes; CRLF and LF both end a record; a trailing
// line break does not produce an empty last row. Values are kept as the text
// they are — this is a lightweight grid, not a spreadsheet engine, so nothing
// is coerced to a number or a date.
export const parseCsv = ({
  text,
  maxRows,
  maxCols,
  maxChars,
}: IParseCsvProps): IParsedCsv => {
  // NOTE: Everything past `maxChars` is treated as absent — the scan, the
  // delimiter sniff and the truncation flag all work off this one limit, so no
  // part of the parser walks further than the caller allowed.
  const limit =
    maxChars === undefined ? text.length : Math.min(text.length, maxChars);
  const delimiter = sniffDelimiter({ text, limit });
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let isQuoted = false;
  let isTruncated = limit < text.length;
  let isColumnsTruncated = false;

  const pushField = (): void => {
    if (maxCols === undefined || row.length < maxCols) {
      row.push(field);
    } else {
      isColumnsTruncated = true;
    }
    field = "";
  };

  const pushRow = (): boolean => {
    pushField();
    if (maxRows !== undefined && rows.length >= maxRows) {
      isTruncated = true;
      return false;
    }
    rows.push(row);
    row = [];
    return true;
  };

  for (let index = 0; index < limit; index += 1) {
    const character = text[index] ?? "";

    if (isQuoted) {
      if (character === DOUBLE_QUOTE) {
        if (text[index + 1] === DOUBLE_QUOTE) {
          field += DOUBLE_QUOTE;
          index += 1;
        } else {
          isQuoted = false;
        }
      } else {
        field += character;
      }
      continue;
    }

    if (character === DOUBLE_QUOTE) {
      isQuoted = true;
    } else if (character === delimiter) {
      pushField();
    } else if (character === LINE_FEED || character === CARRIAGE_RETURN) {
      if (character === CARRIAGE_RETURN && text[index + 1] === LINE_FEED) {
        index += 1;
      }
      if (!pushRow()) {
        return { rows, isTruncated, isColumnsTruncated };
      }
    } else {
      field += character;
    }
  }

  // NOTE: The last record has no terminator when the file lacks a trailing line
  // break; an empty tail (a trailing line break) is not a record.
  if (field.length > 0 || row.length > 0) {
    pushRow();
  }

  return { rows, isTruncated, isColumnsTruncated };
};

// NOTE: Spreadsheet-style column label for a zero-based index (0→A, 25→Z,
// 26→AA, …). Shared by the web grid viewer and the mobile CSV preview so both
// label columns the same way.
export const toSpreadsheetColumnLabel = (index: number): string => {
  let label = "";
  let cursor = index;
  while (cursor >= 0) {
    label =
      String.fromCodePoint((cursor % ALPHABET_SIZE) + LETTER_A_CODE_POINT) +
      label;
    cursor = Math.floor(cursor / ALPHABET_SIZE) - 1;
  }
  return label;
};
